import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2, Download, TrendingUp, BarChart3, Building2, Stethoscope, DollarSign,
  Activity, Users, MapPin, RefreshCw, AlertCircle, CheckCircle2, Briefcase,
  CalendarClock, CreditCard, Map as MapIcon, Award, Percent,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { getReportsSummary, reportCsvUrl, stripeSync } from "@/lib/api";
import DispatchMap from "@/components/dispatch/DispatchMap";

const fmtMoney = (n) =>
  "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const FUNNEL_LABELS = {
  new: "New Leads",
  contacted: "Contacted",
  quoted: "Quoted",
  billed: "Payment Sent",
  paid: "Paid",
  scheduled: "Scheduled",
  completed: "Completed",
  cancelled: "Cancelled",
};

const EXPORT_KINDS = [
  { kind: "revenue", label: "Revenue Report" },
  { kind: "services", label: "Service Report" },
  { kind: "staff", label: "Staff Report" },
  { kind: "facility-leads", label: "Facility Report" },
  { kind: "dispatch", label: "Dispatch Report" },
  { kind: "leads", label: "Consumer Leads" },
  { kind: "contracts", label: "Contracts" },
];

export default function ReportsPanel({ onSessionExpired }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [syncBusy, setSyncBusy] = useState(false);

  const refresh = async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const d = await getReportsSummary();
      setData(d);
    } catch (err) {
      if (err?.response?.status === 401) { onSessionExpired?.(); return; }
      if (!silent) toast.error("Failed to load reports.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { refresh(false); /* eslint-disable-next-line */ }, []);

  const handleStripeSync = async () => {
    setSyncBusy(true);
    try {
      const res = await stripeSync();
      if (res.status === "ok") {
        toast.success(res.message || "Stripe sync complete.");
      } else if (res.status === "not_configured") {
        toast.info(res.message || "Stripe key not configured.");
      } else {
        toast.error(res.message || "Stripe sync failed.");
      }
      refresh(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed.");
    } finally {
      setSyncBusy(false);
    }
  };

  if (loading) {
    return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "#0B2545" }} /></div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="reports-panel">
      <Header refresh={() => refresh(true)} refreshing={refreshing} />

      {/* Executive KPI ribbon */}
      <KpiRibbon revenue={data.revenue} ops={data.operations} />

      {/* Financial */}
      <Section title="Financial" subtitle="Revenue, unpaid balances, and Stripe state">
        <RevenueCards revenue={data.revenue} stripe={data.stripe} onSync={handleStripeSync} syncBusy={syncBusy} />
      </Section>

      {/* Operations dashboard */}
      <Section title="Operations Dashboard" subtitle="Live job status across the board">
        <OpsGrid ops={data.operations} />
      </Section>

      {/* Funnel + Service performance */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <Card title="Lead Conversion Funnel" subtitle="8 stages with conversion %" testid="reports-funnel-extended">
          <FunnelExtended funnel={data.funnel_extended} />
        </Card>
        <Card title="Service Performance" subtitle="Requests · Completed · Revenue per service" testid="reports-service-performance">
          <ServicePerformance rows={data.service_performance} />
        </Card>
      </div>

      {/* Staff + Facility */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <Card title="Staff Performance" subtitle="Jobs completed, revenue, cancellation rate" testid="reports-staff-performance">
          <StaffPerformance rows={data.staff_performance} />
        </Card>
        <Card title="Facility Reporting" subtitle="Active counts and top performers" testid="reports-facility">
          <FacilityReporting facility={data.facility} />
        </Card>
      </div>

      {/* Geographic */}
      <Section title="Geographic Analytics" subtitle="Service density and regions">
        <Geographic geo={data.geographic} />
      </Section>

      {/* Exports */}
      <Section title="Exports" subtitle="Download CSV reports for any time range">
        <ExportGrid />
      </Section>
    </div>
  );
}

// =============================================================================
const Header = ({ refresh, refreshing }) => (
  <div className="flex items-end justify-between flex-wrap gap-3">
    <div>
      <div className="text-[11px] uppercase tracking-[0.22em] font-medium mb-1" style={{ color: "#7A5E12" }}>Records</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "#0B2545" }}>Reports &amp; Analytics</h1>
      <p className="mt-1 text-sm" style={{ color: "#64748B" }}>
        Executive dashboard — finance, operations, funnel, staff, facilities, and geographic insight.
      </p>
    </div>
    <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} data-testid="reports-refresh">
      {refreshing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
      Refresh
    </Button>
  </div>
);

// =============================================================================
const KpiRibbon = ({ revenue, ops }) => {
  const tiles = [
    { label: "Revenue Today", value: fmtMoney(revenue.today), icon: DollarSign, tone: "gold", testid: "kpi-revenue-today" },
    { label: "Revenue This Month", value: fmtMoney(revenue.month), icon: TrendingUp, tone: "green", testid: "kpi-revenue-month" },
    { label: "Active Jobs", value: ops.active_jobs, icon: Activity, tone: "navy", testid: "kpi-active-jobs" },
    { label: "Awaiting Payment", value: ops.awaiting_payment, icon: CreditCard, tone: "red", testid: "kpi-awaiting-payment" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="reports-kpi-ribbon">
      {tiles.map((t, i) => <KpiTile key={i} {...t} />)}
    </div>
  );
};

const TONE = {
  gold: { color: "#7A5E12", bg: "rgba(212,175,55,0.18)" },
  green: { color: "#0F6D5D", bg: "rgba(15,109,93,0.12)" },
  navy: { color: "#0B2545", bg: "rgba(11,37,69,0.08)" },
  red: { color: "#9B1C1C", bg: "rgba(155,28,28,0.10)" },
};

const KpiTile = ({ label, value, icon: Icon, tone = "navy", testid }) => {
  const p = TONE[tone];
  return (
    <div className="rounded-2xl border bg-white p-4" style={{ borderColor: "#E2E8F0" }} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.18em] font-medium" style={{ color: "#64748B" }}>
          {label}
        </span>
        <span className="grid place-items-center h-7 w-7 rounded-lg" style={{ background: p.bg, color: p.color }}>
          <Icon className="h-3.5 w-3.5" />
        </span>
      </div>
      <div className="mt-1 font-display text-2xl font-bold" style={{ color: "#0B2545" }}>{value}</div>
    </div>
  );
};

// =============================================================================
const Section = ({ title, subtitle, children }) => (
  <div>
    <div className="mb-3 flex items-end justify-between flex-wrap gap-2">
      <div>
        <h2 className="font-display text-xl font-semibold" style={{ color: "#0B2545" }}>{title}</h2>
        {subtitle && <p className="text-xs mt-0.5" style={{ color: "#64748B" }}>{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const Card = ({ title, subtitle, children, testid }) => (
  <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E2E8F0" }} data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <BarChart3 className="h-4 w-4" style={{ color: "#7A5E12" }} />
      <h3 className="font-display text-base font-semibold" style={{ color: "#0B2545" }}>{title}</h3>
    </div>
    {subtitle && <p className="text-xs" style={{ color: "#64748B" }}>{subtitle}</p>}
    <div className="mt-3">{children}</div>
  </div>
);

// =============================================================================
// FINANCIAL
const RevenueCards = ({ revenue, stripe, onSync, syncBusy }) => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="reports-revenue-cards">
    <div className="lg:col-span-2 rounded-2xl border bg-white p-5" style={{ borderColor: "#E2E8F0" }}>
      <div className="flex items-center gap-2 mb-2">
        <DollarSign className="h-4 w-4" style={{ color: "#0F6D5D" }} />
        <h3 className="font-display text-base font-semibold" style={{ color: "#0B2545" }}>Revenue</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
        <Mini label="Today" value={fmtMoney(revenue.today)} testid="rev-today" />
        <Mini label="This Week" value={fmtMoney(revenue.week)} testid="rev-week" />
        <Mini label="This Month" value={fmtMoney(revenue.month)} testid="rev-month" />
        <Mini label="This Year" value={fmtMoney(revenue.year)} testid="rev-year" />
        <Mini label="Avg / Job" value={fmtMoney(revenue.avg_per_job)} testid="rev-avg" />
      </div>
    </div>
    <div className="rounded-2xl border bg-white p-5 flex flex-col" style={{ borderColor: "#E2E8F0" }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4" style={{ color: "#7A5E12" }} />
          <h3 className="font-display text-base font-semibold" style={{ color: "#0B2545" }}>Outstanding</h3>
        </div>
        <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded"
              style={{ background: TONE[stripe?.state === "live" ? "green" : stripe?.state === "test" ? "gold" : "red"].bg,
                       color: TONE[stripe?.state === "live" ? "green" : stripe?.state === "test" ? "gold" : "red"].color }}
              data-testid="reports-stripe-state">
          Stripe · {stripe?.state || "not_configured"}
        </span>
      </div>
      <div className="space-y-1.5 mb-3">
        <Row label="Unpaid balance" value={fmtMoney(revenue.unpaid_balance)} tone="red" />
        <Row label="Unpaid count" value={revenue.unpaid_count} tone="red" />
        <Row label="Pending (billed)" value={revenue.pending_count} tone="gold" />
      </div>
      <Button size="sm" variant="outline" className="mt-auto"
              onClick={onSync} disabled={syncBusy}
              data-testid="reports-stripe-sync">
        {syncBusy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
        Sync from Stripe
      </Button>
      {stripe?.last_sync?.synced_at && (
        <p className="text-[10px] mt-2" style={{ color: "#94A3B8" }}>
          Last sync: {stripe.last_sync.synced_at.slice(0, 16).replace("T", " ")}
        </p>
      )}
    </div>
  </div>
);

const Mini = ({ label, value, testid }) => (
  <div className="rounded-lg p-2.5" style={{ background: "rgba(11,37,69,0.04)" }} data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "#64748B" }}>{label}</div>
    <div className="mt-0.5 font-display text-lg font-bold" style={{ color: "#0B2545" }}>{value}</div>
  </div>
);

const Row = ({ label, value, tone = "navy" }) => {
  const p = TONE[tone];
  return (
    <div className="flex items-center justify-between text-xs">
      <span style={{ color: "#475569" }}>{label}</span>
      <span className="font-semibold px-2 py-0.5 rounded" style={{ background: p.bg, color: p.color }}>{value}</span>
    </div>
  );
};

// =============================================================================
// OPERATIONS
const OpsGrid = ({ ops }) => {
  const tiles = [
    { label: "Active Jobs", value: ops.active_jobs, icon: Activity, tone: "navy", testid: "ops-active-jobs" },
    { label: "Scheduled Today", value: ops.scheduled_today, icon: CalendarClock, tone: "gold", testid: "ops-scheduled-today" },
    { label: "Completed Today", value: ops.completed_today, icon: CheckCircle2, tone: "green", testid: "ops-completed-today" },
    { label: "In Dispatch", value: ops.in_dispatch, icon: Briefcase, tone: "navy", testid: "ops-in-dispatch" },
    { label: "Awaiting Assignment", value: ops.awaiting_assignment, icon: AlertCircle, tone: "red", testid: "ops-awaiting-assignment" },
    { label: "Awaiting Payment", value: ops.awaiting_payment, icon: CreditCard, tone: "red", testid: "ops-awaiting-payment" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="reports-ops-grid">
      {tiles.map((t, i) => <KpiTile key={i} {...t} />)}
    </div>
  );
};

// =============================================================================
// FUNNEL (8 stages with %)
const FunnelExtended = ({ funnel }) => {
  if (!funnel) return null;
  const stages = funnel.stages || [];
  const counts = funnel.counts || {};
  const pct = funnel.pct || {};
  const max = Math.max(1, ...stages.map((s) => counts[s] || 0));
  return (
    <ul className="space-y-2.5" data-testid="reports-funnel-list">
      {stages.map((s) => {
        const count = counts[s] || 0;
        const w = (count / max) * 100;
        return (
          <li key={s} data-testid={`funnel-row-${s}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-medium" style={{ color: "#0B2545" }}>{FUNNEL_LABELS[s] || s}</span>
              <span className="flex items-center gap-2">
                <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "rgba(11,37,69,0.06)", color: "#0B2545" }}>
                  {count}
                </span>
                {typeof pct[s] === "number" && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-0.5"
                        style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>
                    <Percent className="h-2.5 w-2.5" /> {pct[s]}%
                  </span>
                )}
              </span>
            </div>
            <div className="h-2 rounded-full" style={{ background: "#F1F5F9" }}>
              <div className="h-full rounded-full" style={{
                width: `${w}%`,
                background: "linear-gradient(90deg, #0B2545 0%, #D4AF37 100%)",
              }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
};

// =============================================================================
// SERVICE PERFORMANCE
const ServicePerformance = ({ rows = [] }) => {
  if (!rows.length) return <Empty text="No service activity yet." />;
  const maxRev = Math.max(1, ...rows.map((r) => r.revenue || 0));
  return (
    <ul className="space-y-2.5" data-testid="reports-service-rows">
      {rows.slice(0, 8).map((r) => (
        <li key={r.service} className="rounded-lg border p-2.5" style={{ borderColor: "#E2E8F0" }}
            data-testid={`service-row-${r.service.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium flex items-center gap-1.5 truncate" style={{ color: "#0B2545" }}>
              <Stethoscope className="h-3 w-3" style={{ color: "#7A5E12" }} />
              {r.service}
            </span>
            <span className="text-xs font-semibold" style={{ color: "#0F6D5D" }}>{fmtMoney(r.revenue)}</span>
          </div>
          <div className="flex items-center gap-2 text-[10px]" style={{ color: "#475569" }}>
            <span><b>{r.requests}</b> requests</span>
            <span>· <b>{r.completed}</b> completed</span>
            <span>· avg <b>{fmtMoney(r.avg_value)}</b></span>
          </div>
          <div className="h-1.5 rounded-full mt-1.5" style={{ background: "#F1F5F9" }}>
            <div className="h-full rounded-full" style={{
              width: `${((r.revenue || 0) / maxRev) * 100}%`,
              background: "#D4AF37",
            }} />
          </div>
        </li>
      ))}
    </ul>
  );
};

// =============================================================================
// STAFF PERFORMANCE
const StaffPerformance = ({ rows = [] }) => {
  if (!rows.length) return <Empty text="No staff assignments yet. Assign a job to a field tech from the Dispatch Center to populate." />;
  return (
    <ul className="space-y-2" data-testid="reports-staff-rows">
      {rows.map((s) => (
        <li key={s.id} className="rounded-lg border p-2.5"
            style={{ borderColor: "#E2E8F0" }}
            data-testid={`staff-row-${s.id}`}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold flex items-center gap-1.5" style={{ color: "#0B2545" }}>
              <Award className="h-3 w-3" style={{ color: "#7A5E12" }} /> {s.name}
            </span>
            <span className="text-xs font-semibold" style={{ color: "#0F6D5D" }}>{fmtMoney(s.revenue)}</span>
          </div>
          <div className="mt-1 grid grid-cols-3 gap-2 text-[10px]">
            <Stat label="Completed" value={s.completed} />
            <Stat label="Workload" value={s.monthly_workload} />
            <Stat label="Cancel rate" value={`${s.cancellation_rate}%`} tone={s.cancellation_rate > 10 ? "red" : "green"} />
          </div>
        </li>
      ))}
    </ul>
  );
};

const Stat = ({ label, value, tone = "navy" }) => {
  const p = TONE[tone];
  return (
    <div className="rounded-md p-1.5" style={{ background: "rgba(11,37,69,0.04)" }}>
      <div className="uppercase tracking-wider" style={{ color: "#94A3B8" }}>{label}</div>
      <div className="font-bold" style={{ color: p.color }}>{value}</div>
    </div>
  );
};

// =============================================================================
// FACILITY
const FacilityReporting = ({ facility }) => {
  if (!facility) return null;
  return (
    <div className="space-y-3" data-testid="reports-facility-content">
      <div className="grid grid-cols-3 gap-2">
        <Mini label="Active Facilities" value={facility.active_facilities} testid="fac-active" />
        <Mini label="Active Contracts" value={facility.active_contracts} testid="fac-contracts" />
        <Mini label="Monthly Revenue" value={fmtMoney(facility.monthly_revenue)} testid="fac-monthly-rev" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.16em] mb-1.5" style={{ color: "#64748B" }}>
          Top Performing Facilities
        </div>
        {(facility.top || []).length === 0 ? (
          <Empty text="No facility-tagged leads yet." />
        ) : (
          <ul className="space-y-1.5" data-testid="reports-top-facilities-list">
            {facility.top.slice(0, 8).map((f) => (
              <li key={f.name} className="flex items-center justify-between rounded-lg px-2.5 py-1.5"
                  style={{ background: "#F8FAFC" }}
                  data-testid={`fac-row-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <span className="flex items-center gap-2 text-sm truncate" style={{ color: "#0B2545" }}>
                  <Building2 className="h-3.5 w-3.5" style={{ color: "#7A5E12" }} />
                  {f.name}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                        style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>
                    {f.count} jobs
                  </span>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                        style={{ background: "rgba(15,109,93,0.12)", color: "#0F6D5D" }}>
                    {fmtMoney(f.revenue)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// GEOGRAPHIC
const Geographic = ({ geo }) => {
  const pins = geo?.pins || [];
  const top = geo?.top_regions || [];
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_280px] gap-3" data-testid="reports-geographic">
      <div>
        <DispatchMap pins={pins} height={320} />
      </div>
      <div className="rounded-2xl border bg-white p-4" style={{ borderColor: "#E2E8F0" }}>
        <div className="flex items-center gap-2 mb-2">
          <MapIcon className="h-4 w-4" style={{ color: "#7A5E12" }} />
          <h3 className="font-display text-sm font-semibold" style={{ color: "#0B2545" }}>Top Service Regions</h3>
        </div>
        {top.length === 0 ? (
          <Empty text="Geocode a few addresses from the Dispatch dialog to populate the map." />
        ) : (
          <ul className="space-y-1.5" data-testid="reports-top-regions">
            {top.map((r) => (
              <li key={r.region} className="flex items-center justify-between rounded-md px-2 py-1.5"
                  style={{ background: "#F8FAFC" }}
                  data-testid={`region-row-${r.region.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <span className="text-sm flex items-center gap-1.5" style={{ color: "#0B2545" }}>
                  <MapPin className="h-3 w-3" style={{ color: "#94A3B8" }} />
                  {r.region}
                </span>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>
                  {r.count}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// EXPORTS
const ExportGrid = () => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="reports-exports">
    {EXPORT_KINDS.map((r) => (
      <a key={r.kind} href={reportCsvUrl(r.kind)}
         className="inline-flex items-center justify-between text-xs font-medium px-3 py-2.5 rounded-lg border transition-colors hover:bg-slate-50"
         style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
         data-testid={`csv-${r.kind}`}>
        <span>{r.label}</span>
        <Download className="h-3.5 w-3.5" style={{ color: "#7A5E12" }} />
      </a>
    ))}
  </div>
);

const Empty = ({ text }) => (
  <div className="text-xs py-4 text-center" style={{ color: "#94A3B8" }}>{text}</div>
);
